import './schedule-practice-week-schedule.scss';

import { useMutation } from '@apollo/client';
import {
  first,
  groupBy,
  isEmpty,
  kebabCase,
  last,
  mapValues,
  orderBy,
} from 'lodash';
import { useCallback, useMemo } from 'react';
import { FormattedDate, useIntl } from 'react-intl';

import { useAppContext } from '../../../data/context/app-context';
import {
  ScheduleConsultType,
  ScheduleDayPartType,
} from '../../../data/data/schedule/enums';
import { useScheduleDateRangeCheck } from '../../../data/data/schedule/use-schedule-date-range-check';
import { useScheduleDateTemplateItems } from '../../../data/data/schedule/use-schedule-date-template-items';
import { PracticeId } from '../../../data/data/types';
import {
  consultTypeToScheduleConsultType,
  getScheduleDataForWeek,
  getScheduleDay,
} from '../../../data/data/utils';
import {
  AgendaAppointmentItemDataFragment,
  ConsultType,
  GetAgendaItemsDocument,
  GetScheduleItemsDocument,
  ScheduleItemDataFragment,
  TimeOfDay,
  UpdateScheduleConsultItemDocument,
} from '../../../data/types/api/graphql';
import { useFormatters } from '../../../data/utils/formatters.utils';
import { makeKeyDownHandler } from '../../../data/utils/make-key-down-handler';
import { useNavigateWithHealthProvider } from '../../../data/utils/router.utils';
import { AlertMessageType } from '../../../elements/alert-message/alert-message';
import { CustomFormattedDate } from '../../../elements/intl/custom-formatted-date';
import { CustomFormattedMessage } from '../../../elements/intl/custom-formatted-message';
import { Tooltip } from '../../../elements/tooltip/tooltip';

type SchedulePracticeWeekScheduleProps = {
  healthProviderId: string | null | undefined;
  healthProfessionalId: string | null | undefined;
  date: {
    weekNr: number | null | undefined;
    year: number | null | undefined;
  };
  data: ScheduleItemDataFragment[];
  editable?: boolean;
};

export function SchedulePracticeWeekSchedule({
  healthProviderId,
  editable = false,
  ...props
}: SchedulePracticeWeekScheduleProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const { isPracticeSchedule, scheduleData } = useMemo(
    () => ({
      isPracticeSchedule: props.healthProfessionalId === PracticeId,
      scheduleData: getScheduleDataForWeek(props.date.weekNr, props.date.year),
    }),
    [props.healthProfessionalId, props.date]
  );

  const { data: scheduleDateRangeCheckData } = useScheduleDateRangeCheck({
    healthProviderId,
    healthProfessionalId: isPracticeSchedule
      ? null
      : props.healthProfessionalId,
    startDate: first(scheduleData.dates),
    endDate: last(scheduleData.dates),
  });

  const {
    data: scheduleDateTemplateItemsData,
    loading: scheduleDateTemplateItemsLoading,
  } = useScheduleDateTemplateItems({
    healthProviderId,
    healthProfessionalId: isPracticeSchedule
      ? null
      : props.healthProfessionalId,
    startDate: first(scheduleData.dates),
    endDate: last(scheduleData.dates),
  });

  const data = useMemo(
    () =>
      mapValues(
        mapValues(groupBy(props.data, 'date'), option =>
          groupBy(option, 'timeOfDay')
        ),
        date =>
          mapValues(date, dayPart =>
            dayPart.map(consult => ({
              ...consult.consultItem,
              consultTypes: consult.consultItem.consultTypes.map(consultType =>
                consultTypeToScheduleConsultType(consultType)
              ),
            }))
          )
      ),
    [props.data]
  );

  const getIsInPast = useCallback(
    (date: string, dayPart?: ScheduleDayPartType | null | undefined) => {
      const now = new Date();

      if (dayPart) {
        if (dayPart === ScheduleDayPartType.MORNING) {
          return new Date(date).setHours(11, 59, 59) < now.getTime();
        }

        if (dayPart === ScheduleDayPartType.AFTERNOON) {
          return new Date(date).setHours(17, 59, 59) < now.getTime();
        }

        if (dayPart === ScheduleDayPartType.EVENING) {
          return new Date(date).setHours(23, 59, 59) < now.getTime();
        }
      }

      return new Date(date).getTime() < new Date().setHours(0, 0, 0);
    },
    []
  );

  const getAppointments = useCallback(
    (
      date: string,
      dayPart: ScheduleDayPartType,
      value: {
        healthProfessionalId: string;
        consultType: ScheduleConsultType;
      }
    ) => {
      const day = getScheduleDay(date)!;

      const currentAppointments =
        scheduleDateRangeCheckData.appointments[day] ?? {};

      return orderBy(
        (currentAppointments[dayPart] ?? []).filter(
          appointment =>
            appointment.healthProfessional?.id === value.healthProfessionalId &&
            consultTypeToScheduleConsultType(appointment.consultType) &&
            consultTypeToScheduleConsultType(appointment.consultType) ===
              value.consultType
        ),
        'startTime'
      );
    },
    [scheduleDateRangeCheckData]
  );

  const getAppointmentsTooltip = useCallback(
    (appointments: AgendaAppointmentItemDataFragment[]) => {
      const getTime = (value: string | null | undefined) => {
        if (value) {
          return intl.formatDate(value, {
            hour: 'numeric',
            minute: 'numeric',
          });
        }

        return null;
      };

      const values = appointments
        .map(appointment => {
          if (appointment.startTime) {
            return `${intl.formatMessage(
              {
                id: 'common.appointment-date',
              },
              {
                date: intl.formatDate(appointment.startTime, {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                }),
                time: [
                  getTime(appointment.startTime),
                  getTime(appointment.endTime),
                ]
                  .filter(option => option)
                  .join(' - '),
              }
            )} : ${intl.formatMessage({
              id: `common.${kebabCase(appointment.consultType)}`,
            })}`;
          }

          return null;
        })
        .filter(appointment => appointment);

      return (
        <>
          {intl.formatMessage({ id: 'schedule.update.error.content' })}
          {values.map(appointment => (
            <span key={appointment}>{appointment}</span>
          ))}
        </>
      );
    },
    [intl]
  );

  const getTemplate = useCallback(
    (date: string, healthProfessionalId: string) => {
      if (scheduleDateTemplateItemsLoading === true) {
        return undefined;
      }
      return (
        scheduleDateTemplateItemsData.find(
          option =>
            option.date === date &&
            option.healthProfessionalId === healthProfessionalId
        )?.template ?? null
      );
    },
    [scheduleDateTemplateItemsData, scheduleDateTemplateItemsLoading]
  );

  const getTemplateToolTip = useCallback(
    (template: { id: string; name: string } | null | undefined) => {
      if (template) {
        return intl.formatMessage(
          { id: 'common.schedule-template' },
          {
            name: template.name,
          }
        );
      }

      return intl.formatMessage({ id: 'common.schedule-no-template' });
    },
    [intl]
  );

  const [
    updateScheduleConsultItem,
    { loading: updateScheduleConsultItemLoading },
  ] = useMutation(UpdateScheduleConsultItemDocument, {
    refetchQueries: [GetScheduleItemsDocument, GetAgendaItemsDocument],
  });

  const onHandleConsultType = useCallback(
    async (
      date: string,
      dayPart: ScheduleDayPartType,
      value: {
        healthProfessionalId: string;
        consultType: ScheduleConsultType;
        status: boolean;
      }
    ) => {
      try {
        const result = await updateScheduleConsultItem({
          variables: {
            healthProviderId,
            healthProfessionalId: value.healthProfessionalId,
            date,
            dayPart: dayPart as unknown as TimeOfDay,
            consultType: value.consultType as unknown as ConsultType,
            status: value.status,
          },
        });

        if (result.data?.scheduleMutateSingle.scheduleConsult?.success) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'schedule.update.success',
            }),
          });
        } else {
          const existingAppointments =
            result.data?.scheduleMutateSingle.scheduleConsult
              ?.existingAppointments ?? [];

          if (existingAppointments.length > 0) {
            showAlertMessage({
              type: AlertMessageType.ERROR,
              message: getAppointmentsTooltip(existingAppointments),
            });
          } else {
            throw new Error(
              'An unknown error has occurred in the server respond.'
            );
          }
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    },
    [
      intl,
      healthProviderId,
      updateScheduleConsultItem,
      showAlertMessage,
      getAppointmentsTooltip,
    ]
  );

  const navigate = useNavigateWithHealthProvider();

  const onHandleDate = useCallback(
    (value: string, date: string) => {
      navigate(`/agenda/${value}/${date}`);
    },
    [navigate]
  );

  const { getFullName } = useFormatters();

  if (isEmpty(scheduleData.dates)) return null;

  const hasEvening = Object.values(data).some(dates =>
    dates?.Evening?.some(v => v.consultTypes?.length > 0)
  );

  return (
    <div className="week-schedule">
      <div className="week-schedule__content">
        <div className="week-schedule-table">
          <div className="week-schedule-table__header">
            <div className="week-schedule-table-item">
              <div className="week-schedule-table-item__column week-schedule-table-item__column--label">
                <p>
                  <CustomFormattedMessage id="common.week-nr" />{' '}
                  {props.date.weekNr}
                </p>
              </div>

              {scheduleData.dates.map(date => {
                const isInPast = getIsInPast(date);

                return (
                  <div
                    key={date}
                    className={`week-schedule-table-item__column${
                      isInPast ? ' inactive' : ''
                    }`}
                  >
                    <p>
                      <FormattedDate value={date} weekday="short" />{' '}
                      <CustomFormattedDate value={date} month="2-digit" />
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="week-schedule-table__content">
            {scheduleData.dayParts
              .filter(dayPart => dayPart !== 'Evening' || hasEvening)
              .map(dayPart => (
                <div key={dayPart} className="week-schedule-table-item">
                  <div className="week-schedule-table-item__column week-schedule-table-item__column--label">
                    <p>
                      <CustomFormattedMessage
                        id={`common.${kebabCase(dayPart)}`}
                      />
                    </p>
                  </div>

                  {scheduleData.dates.map(date => {
                    const currentDate = data[date] ?? {};
                    const currentDateConsults = currentDate[dayPart] ?? [];

                    const isInPast = getIsInPast(date, dayPart);

                    if (editable) {
                      return (
                        <div
                          key={`${date}.${dayPart}`}
                          className={`week-schedule-table-item__column${
                            isInPast ? ' inactive' : ''
                          }`}
                        >
                          {currentDateConsults.map(consult => {
                            const { healthProfessional, consultTypes } =
                              consult;

                            const template = getTemplate(
                              date,
                              healthProfessional.id
                            );
                            const noTemplateClass =
                              template === undefined ? '' : 'no-template';

                            return (
                              <div
                                key={`${date}.${dayPart}${healthProfessional.id}`}
                                className={`week-schedule-health-professional ${
                                  isInPast ? 'locked' : 'unlocked'
                                } ${
                                  template?.id == null
                                    ? noTemplateClass
                                    : 'has-template'
                                }
                              ${
                                consultTypes?.length <= 0
                                  ? 'no-consulttypes'
                                  : 'has-consulttypes'
                              }`}
                              >
                                {isInPast && (
                                  <div className="state">
                                    <Tooltip
                                      message={
                                        <CustomFormattedMessage id="common.schedule-date-pasted" />
                                      }
                                    >
                                      <i className="icon lock" />
                                    </Tooltip>
                                  </div>
                                )}

                                <div className="template">
                                  <Tooltip
                                    message={getTemplateToolTip(template)}
                                  >
                                    <i className="icon info" />
                                  </Tooltip>
                                </div>

                                {isPracticeSchedule && (
                                  <>
                                    <h3>{getFullName(healthProfessional)}</h3>

                                    <p>
                                      <small>
                                        <CustomFormattedMessage
                                          id={`common.honorific.${kebabCase(
                                            healthProfessional.honorific
                                          )}`}
                                        />
                                      </small>
                                    </p>
                                  </>
                                )}

                                <ul>
                                  {Object.values(ScheduleConsultType).map(
                                    consultType => {
                                      const active =
                                        consultTypes.includes(consultType);

                                      const appointments = getAppointments(
                                        date,
                                        dayPart,
                                        {
                                          healthProfessionalId:
                                            healthProfessional.id,
                                          consultType,
                                        }
                                      ).filter(
                                        x =>
                                          x.consultType !== ConsultType.Else &&
                                          x.consultType !==
                                            ConsultType.Exception
                                      );

                                      if (
                                        active &&
                                        !isInPast &&
                                        appointments.length > 0
                                      ) {
                                        return (
                                          <li key={consultType}>
                                            <Tooltip
                                              message={getAppointmentsTooltip(
                                                appointments
                                              )}
                                            >
                                              <i
                                                className={`icon ${kebabCase(
                                                  consultType
                                                )}${
                                                  active
                                                    ? ' active'
                                                    : ' inactive'
                                                }`}
                                              />
                                            </Tooltip>
                                          </li>
                                        );
                                      }

                                      return (
                                        <li key={consultType}>
                                          <button
                                            type="button"
                                            className={`${
                                              active ? 'active' : 'inactive'
                                            }`}
                                            disabled={
                                              isInPast ||
                                              updateScheduleConsultItemLoading
                                            }
                                            onClick={e => {
                                              e.preventDefault();
                                              e.stopPropagation();

                                              onHandleConsultType(
                                                date,
                                                dayPart,
                                                {
                                                  healthProfessionalId:
                                                    healthProfessional.id,
                                                  consultType,
                                                  status: !active,
                                                }
                                              );
                                            }}
                                          >
                                            <i
                                              className={`icon ${kebabCase(
                                                consultType
                                              )}`}
                                            />
                                          </button>
                                        </li>
                                      );
                                    }
                                  )}
                                </ul>
                              </div>
                            );
                          })}
                        </div>
                      );
                    }

                    return (
                      <div
                        key={`${date}.${dayPart}`}
                        className={`week-schedule-table-item__column${
                          isInPast ? ' inactive' : ''
                        }`}
                      >
                        {currentDateConsults.map(consult => {
                          const { healthProfessional, consultTypes } = consult;

                          const template = getTemplate(
                            date,
                            healthProfessional.id
                          );
                          const noTemplateClass =
                            template === undefined ? '' : 'no-template';

                          return (
                            <div
                              key={`${date}.${dayPart}${healthProfessional.id}`}
                              className={`week-schedule-health-professional locked ${
                                template?.id == null
                                  ? noTemplateClass
                                  : 'has-template'
                              } ${
                                consultTypes?.length <= 0
                                  ? 'no-consulttypes'
                                  : 'has-consulttypes'
                              }`}
                              role="button"
                              tabIndex={0}
                              onClick={() =>
                                onHandleDate(healthProfessional.id, date)
                              }
                              onKeyDown={makeKeyDownHandler(() =>
                                onHandleDate(healthProfessional.id, date)
                              )}
                            >
                              <div className="state">
                                <Tooltip
                                  message={
                                    <CustomFormattedMessage id="common.schedule-date-locked" />
                                  }
                                >
                                  <i className="icon lock" />
                                </Tooltip>
                              </div>

                              <div className="template">
                                <Tooltip message={getTemplateToolTip(template)}>
                                  <i className="icon info" />
                                </Tooltip>
                              </div>

                              {isPracticeSchedule && (
                                <>
                                  <h3>{getFullName(healthProfessional)}</h3>

                                  <p>
                                    <small>
                                      <CustomFormattedMessage
                                        id={`common.honorific.${kebabCase(
                                          healthProfessional.honorific
                                        )}`}
                                      />
                                    </small>
                                  </p>
                                </>
                              )}

                              <ul>
                                {Object.values(ScheduleConsultType).map(
                                  consultType => {
                                    const active =
                                      consultTypes.includes(consultType);

                                    return (
                                      <li key={consultType}>
                                        <i
                                          className={`icon ${kebabCase(
                                            consultType
                                          )} ${active ? 'active' : 'inactive'}`}
                                        />
                                      </li>
                                    );
                                  }
                                )}
                              </ul>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
